(function () {
  'use strict';

  function readMatrix(matrixEl) {
    const inputs = matrixEl.querySelectorAll('.ws-qty-matrix__input');
    const sizes = {};
    let total = 0;
    inputs.forEach((i) => {
      const v = parseInt(i.value, 10) || 0;
      if (v > 0) sizes[i.dataset.size] = v;
      total += v;
    });
    return { sizes, total };
  }

  function formatMoney(cents) {
    return '$' + (cents / 100).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function computePriceTotal(matrixEl) {
    const mapEl = matrixEl.querySelector('[data-ws-variant-map]');
    if (!mapEl) return 0;
    let map;
    try { map = JSON.parse(mapEl.textContent); } catch (e) { return 0; }
    const root = matrixEl.closest('[data-ws-pdp]');
    const color = root ? getSelectedColor(root) : null;
    const c = color ? color.toLowerCase() : null;
    let totalCents = 0;
    matrixEl.querySelectorAll('.ws-qty-matrix__input').forEach((i) => {
      const qty = parseInt(i.value, 10) || 0;
      if (qty === 0) return;
      const s = i.dataset.size && i.dataset.size !== 'default' ? i.dataset.size.toLowerCase() : null;
      const hit = Object.keys(map).find((k) => {
        const parts = k.split(' / ').map((p) => p.trim().toLowerCase());
        return (!c || parts.includes(c)) && (!s || parts.includes(s));
      });
      if (hit) totalCents += (map[hit].price || 0) * qty;
    });
    return totalCents;
  }

  function updateTotal(matrixEl) {
    const totalEl = matrixEl.querySelector('[data-ws-qty-total]');
    if (!totalEl) return;
    const { total } = readMatrix(matrixEl);
    totalEl.textContent = `${total} item${total === 1 ? '' : 's'}`;
    const priceEl = matrixEl.querySelector('[data-ws-price-total]');
    if (priceEl) priceEl.textContent = formatMoney(computePriceTotal(matrixEl));
    document.querySelectorAll('[data-ws-blank-summary]').forEach((el) => {
      el.dataset.qty = total;
      el.textContent = total > 0 ? ` — ${total} item${total === 1 ? '' : 's'}` : '';
    });
  }

  function variantFromRenderedSection(root, color, size) {
    const sectionId = root.dataset.wsSectionId;
    const productUrl = root.dataset.wsUrl || `/products/${root.dataset.wsHandle}`;
    const colorButton = Array.from(root.querySelectorAll('[data-ws-color]')).find((button) => button.dataset.wsColor === color);
    const sizeInput = Array.from(root.querySelectorAll('.ws-qty-matrix__input')).find((input) => input.dataset.size === size);
    const optionValueIds = [colorButton?.dataset.wsOptionValueId, sizeInput?.dataset.wsOptionValueId].filter(Boolean);

    if (!sectionId || optionValueIds.length < 2) return Promise.resolve(null);

    const params = new URLSearchParams({
      section_id: sectionId,
      option_values: optionValueIds.join(','),
    });

    return fetch(`${productUrl}?${params.toString()}`)
      .then((r) => {
        if (!r.ok) throw new Error('Variant lookup failed');
        return r.text();
      })
      .then((htmlText) => {
        const html = new DOMParser().parseFromString(htmlText, 'text/html');
        const selectedVariant = html.querySelector('[data-ws-selected-variant]')?.textContent;
        if (!selectedVariant) return null;
        return JSON.parse(selectedVariant);
      })
      .catch(() => null);
  }

  function variantImageSrc(variant) {
    return (
      variant?.featured_media?.preview_image?.src ||
      variant?.featured_image?.src ||
      null
    );
  }

  function updateHeroFromRenderedSection(root, color) {
    const firstSizeInput = root.querySelector('.ws-qty-matrix__input[data-ws-option-value-id]');
    const size = firstSizeInput?.dataset.size;
    if (!size) return Promise.resolve(false);

    return variantFromRenderedSection(root, color, size).then((variant) => {
      const src = variantImageSrc(variant);
      if (!src) return false;
      setHeroImage(root, src);
      return true;
    });
  }

  function variantIdFor(matrixEl, color, size) {
    const mapEl = matrixEl.querySelector('[data-ws-variant-map]');
    if (!mapEl) return Promise.resolve(null);
    const map = JSON.parse(mapEl.textContent);
    const keys = Object.keys(map);
    
    // 1. If only one variant exists, return it
    if (keys.length === 1) return Promise.resolve(map[keys[0]].id);

    // 2. Try matching color and size in title
    const hit = keys.find((k) => {
      const parts = k.split(' / ').map((p) => p.trim().toLowerCase());
      const c = color ? color.toLowerCase() : null;
      const s = size && size !== 'default' ? size.toLowerCase() : null;
      
      const colorMatch = !c || parts.includes(c);
      const sizeMatch = !s || parts.includes(s);
      return colorMatch && sizeMatch;
    });
    if (hit) return Promise.resolve(map[hit].id);

    // 3. Fallback to AJAX lookup if map matching fails
    const root = matrixEl.closest('[data-ws-pdp]');
    return root ? variantFromRenderedSection(root, color, size).then((variant) => variant?.id || null) : Promise.resolve(null);
  }

  async function buyBlanks(matrixEl, color) {
    const { sizes, total } = readMatrix(matrixEl);
    if (total === 0) {
      alert('Pick at least one size and quantity first.');
      return;
    }
    const items = [];
    for (const [size, qty] of Object.entries(sizes)) {
      const id = await variantIdFor(matrixEl, color, size);
      if (id) items.push({ id, quantity: qty });
    }
    if (items.length === 0) {
      alert('Could not find variants for the selected color/sizes. Opening quote request...');
      const root = matrixEl.closest('[data-ws-pdp]');
      if (root) openQuote(root);
      return;
    }
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((r) => {
        if (!r.ok) throw new Error('Cart add failed');
        window.location.href = '/cart';
      })
      .catch(() => {
        alert('Could not add to cart. Opening quote request...');
        const root = matrixEl.closest('[data-ws-pdp]');
        if (root) openQuote(root);
      });
  }

  function getSelectedColor(root) {
    const selected = root.querySelector('[data-ws-color][aria-pressed="true"]');
    return selected ? selected.dataset.wsColor : null;
  }

  function getOrSelectDefaultColor(root) {
    const selectedColor = getSelectedColor(root);
    if (selectedColor) return selectedColor;

    const firstColor = root.querySelector('[data-ws-color]');
    if (!firstColor) return null;

    setSelectedColor(root, firstColor);
    return firstColor.dataset.wsColor || null;
  }

  function openQuote(root) {
    const matrixEl = root.querySelector('[data-ws-qty-matrix]');
    const quote = document.querySelector('[data-ws-quote]');
    if (!quote || !matrixEl) return;
    const { sizes } = readMatrix(matrixEl);
    const colorInput = quote.querySelector('[data-ws-quote-color]');
    const sizesInput = quote.querySelector('[data-ws-quote-sizes]');
    if (colorInput) colorInput.value = getSelectedColor(root) || '';
    if (sizesInput) sizesInput.value = JSON.stringify(sizes);
    quote.hidden = false;
    document.body.style.overflow = 'hidden';
  }

  function closeQuote() {
    const quote = document.querySelector('[data-ws-quote]');
    if (!quote) return;
    quote.hidden = true;
    document.body.style.overflow = '';
  }

  // e.target is not always an Element. focusin/focusout on document fire with e.target set to
  // `document` itself, which has no .matches — an uncaught TypeError on every product page,
  // measured at 55 events across 55 distinct people in the 30 days to 2026-08-24. Guarding the
  // input listener too: it is safe today only because input events come from form controls, and
  // that is a property of the DOM rather than of this code.
  const wsMatches = (target, selector) =>
    target instanceof Element && target.matches(selector);

  document.addEventListener('input', (e) => {
    if (wsMatches(e.target, '.ws-qty-matrix__input')) {
      const matrix = e.target.closest('[data-ws-qty-matrix]');
      if (matrix) updateTotal(matrix);
    }
  });

  document.addEventListener('focusin', (e) => {
    if (wsMatches(e.target, '.ws-qty-matrix__input') && e.target.value === '0') {
      e.target.value = '';
    }
  });

  document.addEventListener('focusout', (e) => {
    if (wsMatches(e.target, '.ws-qty-matrix__input') && e.target.value === '') {
      e.target.value = '0';
      const matrix = e.target.closest('[data-ws-qty-matrix]');
      if (matrix) updateTotal(matrix);
    }
  });

  function setHeroImage(root, src) {
    const img = root.querySelector('[data-ws-hero-img]');
    if (img && src) img.src = src;
  }

  function readRegularVariants(root) {
    const el = root.querySelector('[data-ws-regular-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent) || [];
    } catch (e) {
      return [];
    }
  }

  function getSelectedSize(root) {
    const selected = root.querySelector('[data-ws-size][aria-pressed="true"]');
    return selected ? selected.dataset.wsSize : null;
  }

  function updateRegularVariant(root) {
    const idInput = root.querySelector('[data-ws-regular-variant-id]');
    if (!idInput) return;
    const color = getSelectedColor(root);
    const size = getSelectedSize(root);
    const variants = readRegularVariants(root);
    const match = variants.find((variant) => {
      const opts = variant.options || [];
      return (!color || opts.includes(color)) && (!size || opts.includes(size));
    });
    if (!match) return;
    idInput.value = match.id;
    const submit = root.querySelector('.ws-regular-buy__submit');
    if (submit) {
      submit.disabled = !match.available;
      // Only swap the label. Writing to submit.textContent wipes the button's
      // children, including the .loading__spinner that product-form.js reads on
      // submit — losing it throws before the /cart/add fetch and leaves the
      // button stuck at aria-disabled="true".
      let label = submit.querySelector('span');
      if (!label) {
        label = document.createElement('span');
        submit.prepend(label);
      }
      label.textContent = match.available ? 'Add to Cart' : 'Sold Out';
    }
    if (match.image) setHeroImage(root, match.image);
  }

  function setSelectedColor(root, colorBtn) {
    root.querySelectorAll('[data-ws-color]').forEach((b) => {
      b.setAttribute('aria-pressed', 'false');
      b.classList.remove('is-search-match');
    });
    colorBtn.setAttribute('aria-pressed', 'true');
    const color = colorBtn.dataset.wsColor;
    const nameEl = root.querySelector('[data-ws-color-name]');
    if (nameEl) nameEl.textContent = color;
    const mappedImage = swatchVariantImage(root, color);
    if (mappedImage) {
      setHeroImage(root, mappedImage);
    } else {
      updateHeroFromRenderedSection(root, color);
    }
    updateRegularVariant(root);
  }

  function filterColors(root, query) {
    const normalizedQuery = query.trim().toLowerCase();
    const swatches = Array.from(root.querySelectorAll('[data-ws-color]'));
    const matches = [];
    let visibleCount = 0;
    let exactMatch = null;

    swatches.forEach((swatch) => {
      const color = swatch.dataset.wsColor || '';
      const isVisible = !normalizedQuery || color.toLowerCase().includes(normalizedQuery);
      swatch.hidden = !isVisible;
      swatch.classList.toggle('is-search-match', isVisible && !!normalizedQuery);
      if (isVisible) {
        visibleCount += 1;
        matches.push(swatch);
      }
      if (normalizedQuery && color.toLowerCase() === normalizedQuery) exactMatch = swatch;
    });

    const status = root.querySelector('[data-ws-color-status]');
    if (status) {
      const selected = getSelectedColor(root);
      status.textContent = normalizedQuery
        ? `${visibleCount} color${visibleCount === 1 ? '' : 's'} found${selected ? `; selected ${selected}` : ''}`
        : `${swatches.length} colors available${selected ? `; selected ${selected}` : ''}`;
    }

    updateColorDropdown(root, matches, normalizedQuery);
    if (exactMatch) setSelectedColor(root, exactMatch);
  }

  function closeColorDropdown(root) {
    const dropdown = root.querySelector('[data-ws-color-dropdown]');
    const input = root.querySelector('[data-ws-color-search]');
    if (dropdown) dropdown.hidden = true;
    if (input) {
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
    }
  }

  function updateColorDropdown(root, matches, query) {
    const dropdown = root.querySelector('[data-ws-color-dropdown]');
    const input = root.querySelector('[data-ws-color-search]');
    if (!dropdown || !input) return;

    dropdown.innerHTML = '';
    if (!query || matches.length === 0) {
      closeColorDropdown(root);
      return;
    }

    matches.slice(0, 12).forEach((swatch, index) => {
      const option = document.createElement('button');
      const color = swatch.dataset.wsColor || '';
      option.type = 'button';
      option.id = `${input.id}-option-${index}`;
      option.className = `ws-color-option${index === 0 ? ' is-active' : ''}`;
      option.setAttribute('role', 'option');
      option.dataset.wsColorOption = color;
      option.innerHTML = `<span class="ws-color-option__swatch" aria-hidden="true"></span><span class="ws-color-option__name"></span>`;
      option.querySelector('.ws-color-option__swatch').style.cssText = swatch.style.cssText;
      option.querySelector('.ws-color-option__name').textContent = color;
      dropdown.appendChild(option);
    });

    dropdown.hidden = false;
    input.setAttribute('aria-expanded', 'true');
    input.setAttribute('aria-activedescendant', dropdown.querySelector('.ws-color-option')?.id || '');
  }

  function selectColorOption(root, color) {
    const swatch = Array.from(root.querySelectorAll('[data-ws-color]')).find((button) => button.dataset.wsColor === color);
    if (!swatch) return;
    swatch.hidden = false;
    setSelectedColor(root, swatch);
    const input = root.querySelector('[data-ws-color-search]');
    if (input) input.value = color;
    filterColors(root, color);
    closeColorDropdown(root);
    swatch.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }

  function moveColorDropdownActive(root, direction) {
    const options = Array.from(root.querySelectorAll('.ws-color-option'));
    const input = root.querySelector('[data-ws-color-search]');
    if (!options.length || !input) return;
    const currentIndex = Math.max(0, options.findIndex((option) => option.classList.contains('is-active')));
    const nextIndex = (currentIndex + direction + options.length) % options.length;
    options.forEach((option) => option.classList.remove('is-active'));
    options[nextIndex].classList.add('is-active');
    input.setAttribute('aria-activedescendant', options[nextIndex].id);
    options[nextIndex].scrollIntoView({ block: 'nearest' });
  }

  const wsAjaxColorImages = {};

  function loadAjaxColorImages(root) {
    const handle = root.dataset.wsHandle;
    if (!handle || wsAjaxColorImages[handle]) return Promise.resolve(wsAjaxColorImages[handle] || {});
    return fetch(`/products/${handle}.js`)
      .then((r) => r.json())
      .then((data) => {
        const m = {};
        const colorOption = (data.options || []).find((option) => {
          const name = (option.name || '').toLowerCase();
          return name.includes('color') || name.includes('colour');
        });
        const colorIndex = colorOption ? colorOption.position - 1 : 0;
        (data.variants || []).forEach((v) => {
          const c = Array.isArray(v.options) ? v.options[colorIndex] : v[`option${colorIndex + 1}`];
          if (c && !m[c] && v.featured_image && v.featured_image.src) m[c] = v.featured_image.src;
        });
        wsAjaxColorImages[handle] = m;
        return m;
      })
      .catch(() => ({}));
  }

  function swatchVariantImage(root, color) {
    const ajaxMap = wsAjaxColorImages[root.dataset.wsHandle];
    if (ajaxMap && ajaxMap[color]) return ajaxMap[color];
    const colorMap = root.querySelector('[data-ws-color-images]');
    if (colorMap) {
      try {
        const data = JSON.parse(colorMap.textContent);
        if (data[color]) return data[color];
      } catch (e) {}
    }
    return null;
  }

  function normalizeImageSrc(src) {
    if (!src) return '';
    return src.split('?')[0].replace(/^https?:/, '');
  }

  function colorForImage(root, src) {
    const target = normalizeImageSrc(src);
    if (!target) return null;
    const colorMap = root.querySelector('[data-ws-color-images]');
    let data = {};
    if (colorMap) {
      try {
        data = JSON.parse(colorMap.textContent) || {};
      } catch (e) {
        data = {};
      }
    }
    const ajaxMap = wsAjaxColorImages[root.dataset.wsHandle] || {};
    const merged = { ...data, ...ajaxMap };
    return Object.keys(merged).find((color) => normalizeImageSrc(merged[color]) === target) || null;
  }

  function setSelectedColorByImage(root, src) {
    const color = colorForImage(root, src);
    if (!color) return false;
    const swatch = Array.from(root.querySelectorAll('[data-ws-color]')).find((button) => button.dataset.wsColor === color);
    if (!swatch) return false;
    setSelectedColor(root, swatch);
    return true;
  }

  function initializeProduct(root) {
    const hero = root.querySelector('[data-ws-hero-img]');
    getOrSelectDefaultColor(root);
    loadAjaxColorImages(root).then(() => {
      if (!hero || !setSelectedColorByImage(root, hero.currentSrc || hero.src)) {
        updateRegularVariant(root);
      }
    });
  }

  document.addEventListener('click', (e) => {
    const colorBtn = e.target.closest('[data-ws-color]');
    if (colorBtn) {
      e.preventDefault();
      const root = colorBtn.closest('[data-ws-pdp]');
      if (root) setSelectedColor(root, colorBtn);
      return;
    }
    const thumbBtn = e.target.closest('[data-ws-thumb]');
    if (thumbBtn) {
      e.preventDefault();
      const root = thumbBtn.closest('[data-ws-pdp]');
      if (root) {
        root.querySelectorAll('[data-ws-thumb]').forEach((b) => b.classList.remove('is-active'));
        thumbBtn.classList.add('is-active');
        setHeroImage(root, thumbBtn.dataset.full);
        setSelectedColorByImage(root, thumbBtn.dataset.full);
      }
      return;
    }
    const blankBuy = e.target.closest('[data-ws-blank-buy]');
    if (blankBuy) {
      e.preventDefault();
      const root = blankBuy.closest('[data-ws-pdp]');
      const color = getOrSelectDefaultColor(root);
      buyBlanks(root.querySelector('[data-ws-qty-matrix]'), color);
      return;
    }
    const sizeBtn = e.target.closest('[data-ws-size]');
    if (sizeBtn) {
      e.preventDefault();
      const root = sizeBtn.closest('[data-ws-pdp]');
      if (!root) return;
      root.querySelectorAll('[data-ws-size]').forEach((button) => {
        button.setAttribute('aria-pressed', 'false');
        button.classList.remove('is-active');
      });
      sizeBtn.setAttribute('aria-pressed', 'true');
      sizeBtn.classList.add('is-active');
      updateRegularVariant(root);
      return;
    }
    const openQ = e.target.closest('[data-ws-open-quote]');
    if (openQ) {
      e.preventDefault();
      const root = openQ.closest('[data-ws-pdp]');
      openQuote(root);
      return;
    }
    if (e.target.closest('[data-ws-quote-close]')) {
      closeQuote();
    }
  });

  document.addEventListener('input', (e) => {
    if (!wsMatches(e.target, '[data-ws-color-search]')) return;
    const root = e.target.closest('[data-ws-pdp]');
    if (root) filterColors(root, e.target.value);
  });

  document.addEventListener('keydown', (e) => {
    if (!wsMatches(e.target, '[data-ws-color-search]')) return;
    const root = e.target.closest('[data-ws-pdp]');
    if (!root) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      moveColorDropdownActive(root, 1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      moveColorDropdownActive(root, -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const active = root.querySelector('.ws-color-option.is-active');
      if (active) {
        selectColorOption(root, active.dataset.wsColorOption);
      } else {
        const firstVisible = root.querySelector('[data-ws-color]:not([hidden])');
        if (firstVisible) setSelectedColor(root, firstVisible);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      closeColorDropdown(root);
    }
  });

  document.addEventListener('click', (e) => {
    const option = e.target.closest('[data-ws-color-option]');
    if (!option) return;
    e.preventDefault();
    const root = option.closest('[data-ws-pdp]');
    if (root) selectColorOption(root, option.dataset.wsColorOption);
  });

  document.addEventListener('click', (e) => {
    document.querySelectorAll('[data-ws-pdp]').forEach((root) => {
      if (!root.contains(e.target)) closeColorDropdown(root);
    });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeQuote();
  });

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('[data-ws-pdp]').forEach(initializeProduct);
  });
})();
